import { api } from "@repo/db"
import {
  applyCheckboxFieldChange,
  getFieldSelectOptionLabel,
  getFieldSelectOptionsIncludingValue,
  getFieldRecipientAccess,
  getFieldValueSource,
  isCheckboxValueChecked,
  isCustomerRequiredField,
  isReadonlyField,
} from "@repo/db"
import { Button } from "@repo/shad/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@repo/shad/ui/card"
import { Checkbox } from "@repo/shad/ui/checkbox"
import { Input } from "@repo/shad/ui/input"
import { Label } from "@repo/shad/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@repo/shad/ui/select"
import { StateSelect } from "@repo/ui"
import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useAction } from "convex/react"
import { AlertCircle, CheckCircle2, Loader2, FileSignature } from "lucide-react"
import { useState, useCallback } from "react"
import { Document, Page, pdfjs } from "react-pdf"
import { toast } from "sonner"

import { useConvex } from "@/hooks/use-convex"
import { isUsStateField } from "@/lib/us-state-field"

import "react-pdf/dist/Page/AnnotationLayer.css"
import "react-pdf/dist/Page/TextLayer.css"

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`

export const Route = createFileRoute("/form/$token")({
  component: FormSigningPage,
})

function FormSigningPage() {
  const { token } = Route.useParams()
  const { data: sessionData } = useConvex(api.formCollection.queries.getFormSession, {
    sessionToken: token,
  })
  const updateSession = useMutation(api.formCollection.mutations.updateFormSession)
  const submitSession = useMutation(api.formCollection.mutations.submitFormSession)
  const getDownloadUrl = useAction(api.formCollection.actions.getFormTemplateDownloadUrl)
  const sessionFields = sessionData?.fields ?? []

  const [formData, setFormData] = useState<Record<string, string>>({})
  const [formDataInitialized, setFormDataInitialized] = useState(false)
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [numPages, setNumPages] = useState(0)
  const [pageNumber, setPageNumber] = useState(1)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [customerInfo, setCustomerInfo] = useState({
    name: "",
    email: "",
    phone: "",
  })

  // Load PDF URL when template is available
  const loadPdfUrl = useCallback(async () => {
    if (!sessionData?.template?._id) return
    try {
      const result = await getDownloadUrl({ sessionToken: token })
      setPdfUrl(result.url)
    } catch (err) {
      console.error("Failed to load PDF:", err)
    }
  }, [getDownloadUrl, sessionData?.template?._id, token])

  // Load PDF on mount
  if (sessionData?.template?._id && !pdfUrl) {
    void loadPdfUrl()
  }

  // Initialize form data from session, static defaults, and prefill data
  if (sessionData?.fields && !formDataInitialized) {
    const initialData: Record<string, string> = {}
    const prefillData = sessionData.session?.prefillData as Record<string, string> | undefined
    const savedFormData = sessionData.session?.formData as Record<string, string> | undefined

    for (const field of sessionData.fields) {
      const recipientAccess = getFieldRecipientAccess(field)
      const valueSource = getFieldValueSource(field)

      // Priority: saved form data > prefill data > default value > empty
      if (savedFormData?.[field.fieldName]) {
        initialData[field.fieldName] = savedFormData[field.fieldName] ?? ""
      } else if (
        (valueSource === "merge" || valueSource === "senderInput") &&
        prefillData?.[field.fieldName]
      ) {
        initialData[field.fieldName] = prefillData[field.fieldName] ?? ""
      } else if (
        recipientAccess === "readonly" &&
        valueSource === "constant" &&
        field.defaultValue
      ) {
        initialData[field.fieldName] = field.defaultValue
      } else if (field.defaultValue) {
        initialData[field.fieldName] =
          field.defaultValue === "today"
            ? new Date().toISOString().split("T")[0] || ""
            : field.defaultValue
      } else {
        initialData[field.fieldName] = ""
      }
    }

    setFormData(initialData)
    setFormDataInitialized(true)
  }

  if (sessionData?.session?.customerName && !customerInfo.name) {
    setCustomerInfo({
      name: sessionData.session.customerName || "",
      email: sessionData.session.customerEmail || "",
      phone: sessionData.session.customerPhone || "",
    })
  }

  const handleFieldChange = (fieldName: string, value: string) => {
    setFormData((prev) => {
      const field = sessionFields.find((item) => item.fieldName === fieldName)
      if (!field || field.fieldType !== "checkbox") {
        return { ...prev, [fieldName]: value }
      }

      return applyCheckboxFieldChange(
        sessionFields,
        prev,
        field,
        isCheckboxValueChecked(value),
        (item) => item.fieldName,
        "true",
        "false",
      )
    })
  }

  const handleSave = async () => {
    try {
      await updateSession({
        sessionToken: token,
        formData,
        customerName: customerInfo.name || undefined,
        customerEmail: customerInfo.email || undefined,
        customerPhone: customerInfo.phone || undefined,
      })
      toast.success("Progress saved")
    } catch {
      toast.error("Failed to save")
    }
  }

  const handleSubmit = async () => {
    if (!customerInfo.name || !customerInfo.email) {
      toast.error("Please enter your name and email")
      return
    }

    setIsSubmitting(true)
    try {
      // Save customer info first
      await updateSession({
        sessionToken: token,
        formData,
        customerName: customerInfo.name,
        customerEmail: customerInfo.email,
        customerPhone: customerInfo.phone || undefined,
      })

      // Submit the form
      await submitSession({
        sessionToken: token,
        formData,
      })

      setIsSubmitted(true)
      toast.success("Form submitted successfully")
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to submit")
    } finally {
      setIsSubmitting(false)
    }
  }

  // Loading state
  if (sessionData === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">Loading form...</p>
        </div>
      </div>
    )
  }

  // Not found
  if (!sessionData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-destructive" />
            <h1 className="text-xl font-semibold mb-2">Form Not Found</h1>
            <p className="text-muted-foreground">This form link is invalid or has been removed.</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Expired
  if (sessionData.session?.status === "expired") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-destructive" />
            <h1 className="text-xl font-semibold mb-2">Form Expired</h1>
            <p className="text-muted-foreground">
              This form link has expired. Please contact the dealership for a new link.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Already completed or just submitted
  if (sessionData.session?.status === "completed" || isSubmitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-green-500" />
            <h1 className="text-xl font-semibold mb-2">Form Submitted</h1>
            <p className="text-muted-foreground">
              Thank you! Your form has been submitted successfully.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  const { template, fields, dealership } = sessionData

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Header */}
      <div className="bg-background border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="font-semibold">{template?.name}</h1>
            <p className="text-sm text-muted-foreground">{dealership?.name}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleSave}>
              Save Progress
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Submit
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Form Fields */}
          <div className="lg:col-span-1 space-y-6">
            {/* Customer Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Your Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="customer-name">Full Name *</Label>
                  <Input
                    id="customer-name"
                    value={customerInfo.name}
                    onChange={(e) => setCustomerInfo((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <Label htmlFor="customer-email">Email *</Label>
                  <Input
                    id="customer-email"
                    type="email"
                    value={customerInfo.email}
                    onChange={(e) =>
                      setCustomerInfo((prev) => ({ ...prev, email: e.target.value }))
                    }
                    placeholder="john@example.com"
                  />
                </div>
                <div>
                  <Label htmlFor="customer-phone">Phone</Label>
                  <Input
                    id="customer-phone"
                    type="tel"
                    value={customerInfo.phone}
                    onChange={(e) =>
                      setCustomerInfo((prev) => ({ ...prev, phone: e.target.value }))
                    }
                    placeholder="(555) 123-4567"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Read-only Fields */}
            {fields?.some((f) => isReadonlyField(f)) && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base text-muted-foreground">
                    Pre-filled Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {fields
                    ?.filter((f) => isReadonlyField(f))
                    .map((field) => (
                      <div key={field._id} className="flex justify-between text-sm">
                        <span className="text-muted-foreground">{field.label}</span>
                        <span className="font-medium">
                          {field.fieldType === "checkbox" ? (
                            isCheckboxValueChecked(formData[field.fieldName]) ? (
                              "Yes"
                            ) : (
                              "No"
                            )
                          ) : field.fieldType === "image" && formData[field.fieldName] ? (
                            <img
                              src={formData[field.fieldName]}
                              alt={field.label}
                              className="max-h-12 max-w-24 object-contain"
                            />
                          ) : field.fieldType === "select" ? (
                            getFieldSelectOptionLabel(field, formData[field.fieldName]) ||
                            formData[field.fieldName] ||
                            "—"
                          ) : (
                            formData[field.fieldName] || "—"
                          )}
                        </span>
                      </div>
                    ))}
                </CardContent>
              </Card>
            )}

            {/* Editable Fields (Required/Optional) */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Document Fields</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {fields
                  ?.filter((f) => !isReadonlyField(f))
                  .map((field) => (
                    <div key={field._id}>
                      <Label htmlFor={field._id}>
                        {field.label}
                        {isCustomerRequiredField(field) && " *"}
                      </Label>
                      {field.fieldType === "checkbox" ? (
                        <div className="flex items-center gap-2 mt-1">
                          <Checkbox
                            id={field._id}
                            checked={isCheckboxValueChecked(formData[field.fieldName])}
                            onCheckedChange={(checked) =>
                              handleFieldChange(field.fieldName, checked ? "true" : "false")
                            }
                          />
                          <label htmlFor={field._id} className="text-sm">
                            {field.placeholder || "Check if applicable"}
                          </label>
                        </div>
                      ) : field.fieldType === "signature" || field.fieldType === "initial" ? (
                        <div className="mt-1">
                          {formData[field.fieldName] ? (
                            <div className="border rounded p-2 bg-muted/50">
                              <img
                                src={formData[field.fieldName]}
                                alt={field.fieldType}
                                className="max-h-16"
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleFieldChange(field.fieldName, "")}
                                className="mt-1"
                              >
                                Clear
                              </Button>
                            </div>
                          ) : (
                            <SignaturePad
                              onSave={(dataUrl) => handleFieldChange(field.fieldName, dataUrl)}
                              label={
                                field.fieldType === "initial" ? "Add Initials" : "Add Signature"
                              }
                            />
                          )}
                        </div>
                      ) : field.fieldType === "select" ? (
                        <Select
                          value={formData[field.fieldName] || ""}
                          onValueChange={(value) => handleFieldChange(field.fieldName, value)}
                        >
                          <SelectTrigger id={field._id} className="mt-1">
                            <SelectValue
                              placeholder={
                                field.placeholder || `Select ${field.label.toLowerCase()}`
                              }
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {getFieldSelectOptionsIncludingValue(
                              field,
                              formData[field.fieldName],
                            ).map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : field.fieldType !== "date" &&
                        isUsStateField({
                          definitionKey: field.mergeFieldBinding?.definitionKey,
                          fieldName: field.fieldName,
                          label: field.label,
                          placeholder: field.placeholder,
                        }) ? (
                        <StateSelect
                          value={formData[field.fieldName] || ""}
                          onChange={(value) => handleFieldChange(field.fieldName, value)}
                          placeholder={field.placeholder || `Select ${field.label.toLowerCase()}`}
                          className="mt-1"
                        />
                      ) : (
                        <Input
                          id={field._id}
                          type={field.fieldType === "date" ? "date" : "text"}
                          value={formData[field.fieldName] || ""}
                          onChange={(e) => handleFieldChange(field.fieldName, e.target.value)}
                          placeholder={field.placeholder}
                          className="mt-1"
                        />
                      )}
                    </div>
                  ))}
                {fields?.filter((f) => !isReadonlyField(f)).length === 0 && (
                  <p className="text-sm text-muted-foreground">No fields to fill</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* PDF Preview */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader className="flex-row items-center justify-between">
                <CardTitle className="text-base">Document Preview</CardTitle>
                {numPages > 1 && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                      disabled={pageNumber <= 1}
                    >
                      Previous
                    </Button>
                    <span className="text-sm">
                      Page {pageNumber} of {numPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPageNumber((p) => Math.min(numPages, p + 1))}
                      disabled={pageNumber >= numPages}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                {pdfUrl ? (
                  <div className="border rounded overflow-hidden">
                    <Document
                      file={pdfUrl}
                      onLoadSuccess={({ numPages: pages }: { numPages: number }) =>
                        setNumPages(pages)
                      }
                      loading={
                        <div className="flex items-center justify-center p-8">
                          <Loader2 className="h-6 w-6 animate-spin" />
                        </div>
                      }
                    >
                      <Page
                        pageNumber={pageNumber}
                        width={600}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                      />
                    </Document>
                  </div>
                ) : (
                  <div className="flex items-center justify-center p-8 border rounded bg-muted/50">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}

// Simple signature pad component
function SignaturePad({ onSave, label }: { onSave: (dataUrl: string) => void; label: string }) {
  const [isDrawing, setIsDrawing] = useState(false)
  const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null)

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!canvas) return
    setIsDrawing(true)
    const ctx = canvas.getContext("2d")
    if (!ctx) return
    const rect = canvas.getBoundingClientRect()
    ctx.beginPath()
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top)
  }

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !canvas) return
    const ctx = canvas.getContext("2d")
    if (!ctx) return
    const rect = canvas.getBoundingClientRect()
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top)
    ctx.stroke()
  }

  const stopDrawing = () => {
    setIsDrawing(false)
  }

  const clearCanvas = () => {
    if (!canvas) return
    const ctx = canvas.getContext("2d")
    if (!ctx) return
    ctx.clearRect(0, 0, canvas.width, canvas.height)
  }

  const saveSignature = () => {
    if (!canvas) return
    const dataUrl = canvas.toDataURL("image/png")
    onSave(dataUrl)
  }

  return (
    <div className="space-y-2">
      <canvas
        ref={setCanvas}
        width={300}
        height={100}
        className="border rounded bg-white cursor-crosshair"
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
      />
      <div className="flex gap-2">
        <Button type="button" variant="outline" size="sm" onClick={clearCanvas}>
          Clear
        </Button>
        <Button type="button" size="sm" onClick={saveSignature}>
          <FileSignature className="mr-1 h-3 w-3" />
          {label}
        </Button>
      </div>
    </div>
  )
}
